import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Signpost, Plus, X, RefreshCw, Loader, Save, Search, Mail, FileText, Phone, StickyNote,
  UploadCloud, Users, Send, History, Ban, RotateCcw, EyeOff, Eye, ExternalLink, Pencil, Wand2, CheckCircle, AlertTriangle, Smartphone,
  Inbox as InboxIcon, Forward, PackageCheck, Accessibility, Briefcase, ShieldAlert,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StandingOrderCard from '../components/StandingOrderCard';
import { parseEml } from '../utils/emlParse';

// ── Standing Orders ──────────────────────────────────────────────────────────
// A customer's standing arrangement to have parcels addressed to one place delivered to
// another (first one: Commonwealth Bank — the CBS Building, 35 Tumbalong Blvd → the CBP North
// courier desk, 1-11 Harbour St, Sep 2026). Each order keeps three things together:
//   1. the arrangement itself, in words a driver can act on;
//   2. the drivers it touches and whether each has read the CURRENT wording;
//   3. the paper trail — every email behind it, with sender, date/time and subject copied
//      from the message itself, and the original file.
// Orders are ended, never deleted, and the trail is append-only (a wrong entry is hidden,
// with Restore): the record matters most when somebody asks, a year on, why we deliver there.
//
// Three registers on one page (Paul, 20 Oct 2026), each under its own heading:
//   Customers with a disability — an Authority to Leave that exists because Australia Post
//                                 approved an Accessibility & Disability accommodation;
//   Authority to Leave          — every parcel for an address is left in its receptacle,
//                                 signature items included;
//   Deliver elsewhere           — the original redirect orders.
// An ATL is usually recorded straight from the customer case (Customer Cases → "Record
// Authority to Leave"), which files the case PDF and AP's emails on the trail.

const URL = '/.netlify/functions/standing-orders-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const MAX_MB = 4; // base64 inflates ~33%; Netlify's function body limit is 6 MB
const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%', boxSizing: 'border-box' };
const sectionTitle = { display: 'flex', alignItems: 'center', gap: 7, fontSize: '.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', margin: '22px 0 10px' };

const ROLE_LABEL = { from: 'Delivers the addressed-to building', to: 'Delivers the deliver-to building', both: 'Delivers both', cover: 'Covers the area' };
const ATL_ROLE_LABEL = { from: 'Delivers this address', cover: 'Covers the area' };
// The four registers, in the order the page shows them.
const SECTIONS = [
  { key: 'disability', label: 'Customers with a disability', icon: Accessibility, color: '#2563eb',
    blurb: 'Authority to Leave approved by Australia Post as an Accessibility & Disability accommodation. The customer cannot collect from the Post Office — a carded parcel is a parcel they never get.' },
  { key: 'atl', label: 'Authority to Leave', icon: PackageCheck, color: '#16a34a',
    blurb: 'Every parcel for the address is left in the receptacle there. Drivers confirm it every working day.' },
  { key: 'care', label: 'Take special care', icon: ShieldAlert, color: '#dc2626',
    blurb: 'Addresses where we let the customer down and they are watching. Nothing changes about where the parcel goes: the driver on the round is reminded every working day to deliver it by the book.' },
  { key: 'redirect', label: 'Deliver elsewhere', icon: Signpost, color: 'var(--hp-gold)',
    blurb: 'Parcels addressed to one place, delivered to another by arrangement.' },
];
const sectionOf = (o) => (o.kind === 'atl' ? (o.disability ? 'disability' : 'atl') : o.kind === 'care' ? 'care' : 'redirect');
// ATL and take-special-care are about one address: no deliver-to, drivers confirm daily.
const oneAddress = (kind) => kind === 'atl' || kind === 'care';
const KIND = {
  email: { icon: Mail, label: 'Email' },
  file: { icon: FileText, label: 'Document' },
  call: { icon: Phone, label: 'Phone call' },
  note: { icon: StickyNote, label: 'Note' },
};

const short = (a) => (a || '').split(',')[0].trim();
const fmtDay = (d) => (d ? new Date(d.length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' }) : '—');
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney' });
};
// datetime-local ↔ ISO, in the browser's own zone (the office is in Sydney).
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : '');

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
const isEml = (f) => /\.eml$/i.test(f.name) || f.type === 'message/rfc822';

const emptyFields = (kind = 'redirect') => ({
  kind, disability: false, receptacle: '', include_signature: true, ad_details: '', case_number: '', case_id: '',
  customer: '', contact: '', applies_to: '', instruction: '', office_notes: '',
  from: { name: '', address: '' }, to: { name: '', address: '' },
  drivers: [], starts_on: new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }), review_on: '',
});

export default function StandingOrdersPage() {
  const { addNotification } = useApp();
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('active');
  const [section, setSection] = useState('all');   // 'all' | 'disability' | 'atl' | 'redirect'
  const [drawer, setDrawer] = useState(null); // { mode:'add', initial?, inboxId? } | { mode:'view', id }
  const [inbox, setInbox] = useState([]);
  // ?open=<id> — Customer Cases links straight to the ATL it just recorded.
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get('open') || '';
  useEffect(() => { if (openParam) setDrawer({ mode: 'view', id: openParam }); }, [openParam]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('list');
      if (r.success) { setOrders(r.orders || []); setDrivers(r.drivers || []); }
      else addNotification && addNotification(r.error || 'Could not load standing orders', 'error');
      const ib = await api('inbox');
      if (ib.success) setInbox(ib.items || []);
    } catch (e) { addNotification && addNotification('Network error loading standing orders', 'error'); }
    setLoading(false);
  }, [addNotification]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return orders.filter((o) => (status === 'all' || o.status === status)).filter((o) => {
      if (!n) return true;
      const hay = [
        o.customer, o.contact, o.applies_to, o.instruction, o.office_notes, o.receptacle, o.ad_details, o.case_number,
        o.kind === 'atl' ? 'atl authority to leave' : o.kind === 'care' ? 'care take special care complaint' : 'redirect deliver elsewhere', o.disability ? 'disability accessibility a&d' : '',
        o.from && o.from.name, o.from && o.from.address, o.to && o.to.name, o.to && o.to.address,
        ...(o.drivers || []).map((d) => d.login),
        ...(o.evidence || []).flatMap((e) => [e.from_name, e.from_email, e.organisation, e.subject, e.summary, e.body]),
      ].join(' ').toLowerCase();
      return hay.includes(n);
    });
  }, [orders, q, status]);
  const counts = useMemo(() => {
    const c = { disability: 0, atl: 0, care: 0, redirect: 0 };
    for (const o of orders) if (o.status === 'active') c[sectionOf(o)] += 1;
    return c;
  }, [orders]);
  const visibleSections = SECTIONS.filter((x) => section === 'all' || section === x.key);

  const unread = orders.filter((o) => o.status === 'active').reduce((n, o) => n + [...(o.drivers || []), ...(o.leaders || [])].filter((d) => !d.acked_at).length, 0);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Signpost size={22} style={{ color: 'var(--hp-gold)' }} /> Standing Orders</h2>
          <p>
            Customers with a disability, Authority to Leave, and parcels delivered elsewhere — each with the case or emails that agreed it.
            {unread > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}> · {unread} confirmation{unread === 1 ? '' : 's'} outstanding</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'add', initial: { ...emptyFields('atl'), disability: true } })}><Accessibility size={14} /> New ATL — disability</button>
          <button className="btn btn-secondary" onClick={() => setDrawer({ mode: 'add', initial: emptyFields('atl') })}><PackageCheck size={14} /> New ATL</button>
          <button className="btn btn-secondary" onClick={() => setDrawer({ mode: 'add', initial: emptyFields('care') })}><ShieldAlert size={14} /> Take special care</button>
          <button className="btn btn-secondary" onClick={() => setDrawer({ mode: 'add' })}><Plus size={14} /> Deliver elsewhere</button>
        </div>
      </div>

      <div className="page-body">
        {/* The three registers as tabs — counts are what is in force. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button className={`btn btn-sm ${section === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSection('all')}>Everything</button>
          {SECTIONS.map((x) => (
            <button key={x.key} className={`btn btn-sm ${section === x.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSection(x.key)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <x.icon size={14} /> {x.label} <span style={{ fontFamily: 'var(--font-mono)', opacity: 0.8 }}>{counts[x.key]}</span>
            </button>
          ))}
        </div>
        <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 260px' }}>
            <label style={labelStyle}>Search everything — addresses, customer, case number, drivers, emails</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
              <input className="input" style={{ ...inputStyle, paddingLeft: 30 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Mitchell St, 74101158, Tumbalong, Commonwealth…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Show</label>
            <select className="input" style={{ ...inputStyle, width: 150 }} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">In force</option>
              <option value="ended">Ended</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <InboxPanel
          items={inbox}
          orders={orders}
          notify={addNotification}
          onChanged={load}
          onTriage={(it) => setDrawer({ mode: 'add', inboxId: it.id, initial: draftFrom(it) })}
        />

        {loading ? (
          <div className="card" style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
        ) : (
          visibleSections.map((x) => {
            const list = shown.filter((o) => sectionOf(o) === x.key);
            return (
              <div key={x.key} style={{ marginBottom: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `2px solid ${x.color}`, paddingBottom: 6, marginBottom: 6 }}>
                  <x.icon size={18} style={{ color: x.color }} />
                  <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{x.label}</h3>
                  <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{list.length}</span>
                </div>
                <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 10 }}>{x.blurb}</div>
                {list.length === 0
                  ? <div className="card" style={{ padding: 16, color: 'var(--hp-dim)', fontSize: '.86rem' }}>{q ? 'None match.' : 'None recorded.'}{x.key !== 'redirect' && !q ? ' Record one from the customer case (Customer Cases → Record Authority to Leave), or with the button above.' : ''}</div>
                  : <div style={{ display: 'grid', gap: 12 }}>{list.map((o) => <OrderRow key={o.id} o={o} onOpen={() => setDrawer({ mode: 'view', id: o.id })} />)}</div>}
              </div>
            );
          })
        )}
      </div>

      {drawer && (
        <OrderDrawer
          drawer={drawer}
          drivers={drivers}
          notify={addNotification}
          onClose={() => setDrawer(null)}
          onChanged={load}
          onCreated={(id) => { setDrawer({ mode: 'view', id }); load(); }}
        />
      )}
    </div>
  );
}

function OrderRow({ o, onOpen }) {
  const ended = o.status !== 'active';
  const trail = (o.evidence || []).filter((e) => !e.hidden);
  return (
    <div className="card" onClick={onOpen} style={{ padding: 16, cursor: 'pointer', opacity: ended ? 0.7 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: '1rem' }}>{o.customer || 'Customer not named'}</span>
        <KindChips o={o} />
        {ended
          ? <span style={{ fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 5, padding: '1px 6px' }}>Ended {fmtDay(o.ended_at)}</span>
          : <span style={{ fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', color: '#16a34a', border: '1px solid #16a34a', borderRadius: 5, padding: '1px 6px' }}>In force since {fmtDay(o.starts_on)}</span>}
        {o.review_on && !ended && <span style={{ fontSize: '.72rem', color: '#d97706' }}>review {fmtDay(o.review_on)}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '.76rem', color: 'var(--hp-dim)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Mail size={12} /> {trail.length} record{trail.length === 1 ? '' : 's'} in the trail
        </span>
      </div>
      {o.kind === 'care' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, fontSize: '.9rem' }}>
          <span style={{ color: '#dc2626', fontWeight: 700 }}>{o.from.name ? `${o.from.name}, ` : ''}{o.from.address}</span>
          {o.instruction && <span style={{ color: 'var(--hp-dim)' }}>· {o.instruction.split('\n')[0].slice(0, 110)}</span>}
        </div>
      ) : o.kind === 'atl' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, fontSize: '.9rem' }}>
          <span style={{ color: '#16a34a', fontWeight: 700 }}>{o.from.name ? `${o.from.name}, ` : ''}{o.from.address}</span>
          <span style={{ color: 'var(--hp-dim)' }}>· leave all in {o.receptacle || 'the receptacle'}{o.include_signature ? ' · signature items included' : ''}</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, fontSize: '.9rem' }}>
          <span style={{ color: '#dc2626', fontWeight: 700 }}>{o.from.name ? `${o.from.name}, ` : ''}{short(o.from.address)}</span>
          <span style={{ color: 'var(--hp-gold)', fontWeight: 800 }}>→</span>
          <span style={{ color: '#16a34a', fontWeight: 700 }}>{o.to.name ? `${o.to.name}, ` : ''}{short(o.to.address)}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {(o.drivers || []).length === 0 && <span style={{ fontSize: '.78rem', color: '#d97706' }}><AlertTriangle size={12} /> No drivers named — nobody is being told</span>}
        {(o.drivers || []).map((d) => <DriverChip key={d.login} d={d} ended={ended} />)}
        {(o.leaders || []).map((d) => <DriverChip key={d.login} d={d} ended={ended} leader />)}
      </div>
    </div>
  );
}

function KindChips({ o }) {
  const chip = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', color: c, border: `1px solid ${c}`, borderRadius: 5, padding: '1px 6px' });
  return (
    <>
      {o.kind === 'atl' && <span style={chip('#16a34a')}><PackageCheck size={11} /> ATL</span>}
      {o.kind === 'care' && <span style={chip('#dc2626')}><ShieldAlert size={11} /> Take special care</span>}
      {o.disability && <span style={chip('#2563eb')}><Accessibility size={11} /> Disability</span>}
      {o.case_number && <span style={chip('var(--hp-dim)')}><Briefcase size={11} /> Case {o.case_number}</span>}
    </>
  );
}

function DriverChip({ d, ended, leader }) {
  const ok = !!d.acked_at;
  const c = ended ? 'var(--hp-dim)' : ok ? '#16a34a' : '#ef4444';
  const words = d.daily
    ? (ok ? 'confirmed today' : d.last_acked_at ? `not today · last ${fmtDay(d.last_acked_at)}` : 'not confirmed yet')
    : (ok ? `read ${fmtDay(d.acked_at)}` : 'not read');
  return (
    <span title={`${d.route ? `Tied to round ${d.route} — whoever holds it gets this.${d.was ? ` Took over from ${d.was}.` : ''} ` : leader ? '' : 'Not tied to a round — stays with this driver. '}${ok ? `Confirmed ${fmtWhen(d.acked_at)}` : d.daily ? 'Confirms this every working day — not yet today' : 'Has not read the current wording'}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.76rem', fontFamily: 'var(--font-mono)', fontWeight: 700, border: `1px solid ${c}`, color: c, borderRadius: 6, padding: '2px 8px' }}>
      {ok ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}{leader ? 'TL ' : ''}{d.login}{d.auto ? ' (round now)' : ''}
      {d.route_label && <span style={{ fontFamily: 'inherit', fontWeight: 400 }}>· {d.route_label}</span>}
      <span style={{ fontFamily: 'inherit', fontWeight: 400 }}>{words}</span>
    </span>
  );
}

// ── Drawer ────────────────────────────────────────────────────────────────────
function OrderDrawer({ drawer, drivers, notify, onClose, onChanged, onCreated }) {
  const [order, setOrder] = useState(null);
  const [edit, setEdit] = useState(drawer.mode === 'add');
  const [busy, setBusy] = useState(false);
  const [previewLogin, setPreviewLogin] = useState('');
  const [showHidden, setShowHidden] = useState(false);

  const reload = useCallback(async () => {
    if (drawer.mode !== 'view') return;
    const r = await api('get', { id: drawer.id });
    if (r.success) setOrder(r.order);
    else notify && notify(r.error || 'Could not open that order', 'error');
  }, [drawer, notify]);
  useEffect(() => { reload(); }, [reload]);

  const refresh = async () => { await reload(); onChanged && onChanged(); };

  const send = async () => {
    if (!window.confirm(`Alert ${[...order.drivers, ...(order.leaders || [])].map((d) => d.login).join(', ')} now? Push if they have alerts on, a text if not.`)) return;
    setBusy(true);
    const r = await api('notify', { id: order.id });
    setBusy(false);
    if (r.success) notify && notify(`Sent: ${r.results.map((x) => `${x.login} by ${x.how}`).join(', ')}`, 'success');
    else notify && notify(r.error || 'Could not send', 'error');
    refresh();
  };

  const end = async () => {
    const reason = window.prompt('Why is this arrangement ending? (kept on the record — e.g. "Customer emailed 3 Mar: mailroom reopened")');
    if (reason === null) return;
    setBusy(true);
    const r = await api('setStatus', { id: order.id, status: 'ended', reason });
    setBusy(false);
    if (!r.success) notify && notify(r.error || 'Could not end it', 'error');
    refresh();
  };
  const reinstate = async () => {
    setBusy(true);
    const r = await api('setStatus', { id: order.id, status: 'active' });
    setBusy(false);
    if (!r.success) notify && notify(r.error || 'Could not reinstate it', 'error');
    refresh();
  };

  const people = order ? [...(order.drivers || []), ...(order.leaders || []).map((l) => ({ ...l, role: 'leader', note: '' }))] : [];
  const previewDriver = people.find((d) => d.login === previewLogin) || people[0];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div className="card" style={{ width: 'min(680px, 100%)', height: '100%', overflowY: 'auto', borderRadius: 0, padding: 20, boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            {((order && order.kind) || (drawer.initial && drawer.initial.kind)) === 'atl'
              ? <PackageCheck size={18} style={{ color: '#16a34a' }} />
              : ((order && order.kind) || (drawer.initial && drawer.initial.kind)) === 'care'
                ? <ShieldAlert size={18} style={{ color: '#dc2626' }} />
                : <Signpost size={18} style={{ color: 'var(--hp-gold)' }} />}
            {drawer.mode === 'add'
              ? (drawer.initial && drawer.initial.kind === 'atl' ? `New Authority to Leave${drawer.initial.disability ? ' — customer with a disability' : ''}`
                : drawer.initial && drawer.initial.kind === 'care' ? 'New take-special-care address' : 'New deliver-elsewhere order')
              : (order ? order.customer || (order.kind === 'atl' ? 'Authority to Leave' : order.kind === 'care' ? 'Take special care' : 'Standing order') : 'Loading…')}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {edit ? (
          <OrderForm
            initial={order ? {
              kind: order.kind || 'redirect', disability: !!order.disability, receptacle: order.receptacle || '',
              include_signature: order.include_signature !== false, ad_details: order.ad_details || '',
              case_number: order.case_number || '', case_id: order.case_id || '',
              customer: order.customer, contact: order.contact, applies_to: order.applies_to, instruction: order.instruction,
              office_notes: order.office_notes, from: { ...order.from }, to: { ...order.to },
              // `auto` = on the round now, from the routes: shown, never saved onto the order.
              drivers: (order.drivers || []).filter((d) => !d.auto).map(({ login, role, note, via, route }) => ({ login, role, note, via, route })),
              starts_on: order.starts_on, review_on: order.review_on,
            } : (drawer.initial || emptyFields())}
            id={order && order.id}
            inboxId={!order ? drawer.inboxId : ''}
            drivers={drivers}
            notify={notify}
            onCancel={() => (order ? setEdit(false) : onClose())}
            onSaved={(id) => { setEdit(false); if (!order) onCreated(id); else refresh(); }}
          />
        ) : !order ? (
          <div style={{ color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
        ) : (
          <>
            {order.status !== 'active' && (
              <div style={{ background: 'rgba(100,116,139,.12)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 12px', fontSize: '.85rem', marginBottom: 12 }}>
                <strong>Ended {fmtDay(order.ended_at)}</strong>{order.ended_by ? ` by ${order.ended_by}` : ''}{order.ended_reason ? ` — ${order.ended_reason}` : ''}. Drivers no longer see it; the record stays.
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}><KindChips o={order} /></div>
            {order.kind === 'care' ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <PlaceBox tone="red" label="Take special care at" p={order.from} />
                {order.case_number && (
                  <Info label="Customer case">
                    <a href={`/customer-cases?q=${encodeURIComponent(order.case_number)}`} style={{ color: 'var(--hp-gold)' }}>Case {order.case_number} <ExternalLink size={11} /></a>
                    <span style={{ color: 'var(--hp-dim)' }}> — the case PDF and AP&rsquo;s emails are on the paper trail below.</span>
                  </Info>
                )}
              </div>
            ) : order.kind === 'atl' ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <PlaceBox tone="green" label="Authority to Leave — every parcel for" p={order.from} />
                <Info label="Leave them in">{order.receptacle || 'the parcel receptacle at the property'}</Info>
                <div style={{ fontSize: '.86rem', fontWeight: 700, color: order.include_signature ? '#b45309' : 'var(--hp-dim)' }}>
                  {order.include_signature ? 'Signature items included — the driver leaves them too, never cards them.' : 'Signature items are NOT included — those are carded as normal.'}
                </div>
                {order.disability && (
                  <div style={{ border: '1px solid #2563eb', background: 'rgba(37,99,235,.07)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ ...labelStyle, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 5 }}><Accessibility size={12} /> Customer with a disability — A&amp;D accommodation</div>
                    <div style={{ fontSize: '.86rem', whiteSpace: 'pre-wrap' }}>{order.ad_details || 'No details recorded.'}</div>
                  </div>
                )}
                {order.case_number && (
                  <Info label="Customer case">
                    <a href={`/customer-cases?q=${encodeURIComponent(order.case_number)}`} style={{ color: 'var(--hp-gold)' }}>Case {order.case_number} <ExternalLink size={11} /></a>
                    <span style={{ color: 'var(--hp-dim)' }}> — the case PDF and AP&rsquo;s emails are on the paper trail below.</span>
                  </Info>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                <PlaceBox tone="red" label="Parcels addressed to" p={order.from} />
                <div style={{ textAlign: 'center', color: 'var(--hp-gold)', fontWeight: 800 }}>↓ delivered instead to</div>
                <PlaceBox tone="green" label="Deliver to" p={order.to} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 14, fontSize: '.88rem' }}>
              <Info label="Customer">{order.customer || '—'}</Info>
              <Info label="Customer contact">{order.contact || '—'}</Info>
              <Info label="In force since">{fmtDay(order.starts_on)}</Info>
              <Info label="Review on">{order.review_on ? fmtDay(order.review_on) : 'No review date'}</Info>
            </div>
            {order.applies_to && <Info label="What it covers" style={{ marginTop: 12 }}>{order.applies_to}</Info>}
            {order.instruction && <Info label="What the drivers are told" style={{ marginTop: 12 }}><span style={{ whiteSpace: 'pre-wrap' }}>{order.instruction}</span></Info>}
            {order.office_notes && <Info label="Office notes (drivers don't see these)" style={{ marginTop: 12 }}><span style={{ whiteSpace: 'pre-wrap' }}>{order.office_notes}</span></Info>}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEdit(true)}><Pencil size={13} /> Edit</button>
              {order.status === 'active'
                ? <button className="btn btn-ghost btn-sm" disabled={busy} onClick={end}><Ban size={13} /> End this arrangement</button>
                : <button className="btn btn-ghost btn-sm" disabled={busy} onClick={reinstate}><RotateCcw size={13} /> Put back in force</button>}
            </div>

            {/* ── Drivers ── */}
            <div style={sectionTitle}><Users size={14} /> Drivers told</div>
            <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 6 }}>Each driver is tied to their round (bay · route). When someone new takes the round over, it moves to them automatically and they are asked to confirm it.</div>
            {(order.drivers || []).length === 0
              ? <div style={{ fontSize: '.85rem', color: '#d97706' }}>No drivers named. <strong>Edit</strong> → Find drivers.</div>
              : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {order.drivers.map((d) => (
                    <div key={d.login} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>{d.login}</span>
                      <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
                        {(oneAddress(order.kind) ? ATL_ROLE_LABEL : ROLE_LABEL)[d.role] || d.role}
                        {d.route_label ? <> · <strong style={{ color: 'var(--hp-text)' }}>{d.route_label}</strong></> : ' · not tied to a round (stays with this driver)'}
                        {d.was ? ` · took over the round from ${d.was}` : ''}
                        {d.via ? ` · route has “${d.via}”` : ''}{d.auto ? ' · on the round now (from the routes, not saved on the order)' : ''}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: '.8rem', fontWeight: 700, color: d.acked_at ? '#16a34a' : '#ef4444' }}>
                        {d.daily
                          ? (d.acked_at ? `✓ Confirmed today ${fmtWhen(d.acked_at).split(', ').pop()}` : order.status === 'active' ? `Not confirmed today${d.last_acked_at ? ` (last ${fmtDay(d.last_acked_at)})` : ''}` : '—')
                          : (d.acked_at ? `✓ Read ${fmtWhen(d.acked_at)}` : order.status === 'active' ? 'Not read yet' : '—')}
                      </span>
                      {d.daily && (d.days || []).length > 0 && (
                        <span style={{ flexBasis: '100%', fontSize: '.74rem', color: 'var(--hp-dim)' }}>
                          Confirmed on {d.days.length} day{d.days.length === 1 ? '' : 's'} · last 5: {d.days.slice(-5).map((x) => fmtDay(x)).join(', ')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            {(order.leaders || []).length > 0 && (
              <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                {order.leaders.map((d) => (
                  <div key={d.login} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', border: '1px dashed var(--hp-border)', borderRadius: 8, padding: '8px 10px' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>{d.login}</span>
                    <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>Team leader, {d.areas.join(' & ')} · covers {d.team.map((t) => t.login).join(', ')}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '.8rem', fontWeight: 700, color: d.acked_at ? '#16a34a' : '#ef4444' }}>
                      {d.acked_at ? `✓ Read ${fmtWhen(d.acked_at)}` : order.status === 'active' ? 'Not read yet' : '—'}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>Team leaders are added automatically from the drivers’ areas, so they cover the round when the driver is off.</div>
              </div>
            )}
            {order.status === 'active' && (order.drivers || []).length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={send}><Send size={13} /> Alert the drivers now</button>
                <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>{oneAddress(order.kind)
                  ? 'It is already on their portal — red on the shift screen every working day until they tap Got it, and a 6am push each morning. This sends one now (a text if they have no alerts on).'
                  : 'It is already on their portal (red card on the shift screen until they tap Got it). This adds a push, or a text if they have no alerts on.'}</span>
              </div>
            )}

            {/* ── Preview ── */}
            {(order.drivers || []).length > 0 && (
              <>
                <div style={sectionTitle}><Smartphone size={14} /> What the driver sees</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {people.map((d) => (
                    <button key={d.login} className={`btn btn-sm ${previewDriver && previewDriver.login === d.login ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPreviewLogin(d.login)}>{d.login}</button>
                  ))}
                </div>
                {previewDriver && (
                  <div style={{ background: '#0b0d12', borderRadius: 18, padding: 14, maxWidth: 380, border: '6px solid #1f2937', '--hp-surface': '#151922', '--hp-border': '#2a3140', '--hp-text': '#f3f4f6', '--hp-dim': '#9ca3af', '--hp-gold': '#d4a843', '--hp-green': '#22c55e', '--radius': '12px' }}>
                    <StandingOrderCard preview o={{
                      kind: order.kind, disability: order.disability, receptacle: order.receptacle, include_signature: order.include_signature,
                      case_number: order.case_number, daily: !!previewDriver.daily,
                      id: order.id, customer: order.customer, applies_to: order.applies_to, instruction: order.instruction,
                      from: order.from, to: order.to, role: previewDriver.role, note: previewDriver.note, team: previewDriver.team, starts_on: order.starts_on,
                      me: previewDriver.login,
                      people: (order.drivers || []).map((d) => ({ login: d.login, role: d.role, first: d.first_name })),
                      acked_at: previewDriver.acked_at,
                    }} />
                  </div>
                )}
              </>
            )}

            {/* ── Paper trail ── */}
            <div style={sectionTitle}><Mail size={14} /> Paper trail</div>
            <Trail order={order} showHidden={showHidden} notify={notify} onChanged={refresh} />
            {(order.evidence || []).some((e) => e.hidden) && (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowHidden((v) => !v)}>
                {showHidden ? <><EyeOff size={13} /> Hide hidden records</> : <><Eye size={13} /> Show {(order.evidence || []).filter((e) => e.hidden).length} hidden</>}
              </button>
            )}
            <AddEvidence orderId={order.id} notify={notify} onAdded={refresh} />

            {/* ── History ── */}
            <div style={sectionTitle}><History size={14} /> History</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {[...(order.history || [])].reverse().map((h, i) => (
                <div key={i} style={{ fontSize: '.8rem', display: 'flex', gap: 8 }}>
                  <span style={{ color: 'var(--hp-dim)', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>{fmtWhen(h.at)}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{h.by}</span>
                  <span>{h.what}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PlaceBox({ tone, label, p }) {
  const c = tone === 'red' ? '#dc2626' : '#16a34a';
  return (
    <div style={{ border: `2px solid ${c}`, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: c }}>{label}</div>
      {p.name && <div style={{ fontWeight: 800, marginTop: 4 }}>{p.name}</div>}
      <div style={{ fontSize: '.92rem', marginTop: 2 }}>{p.address}</div>
    </div>
  );
}

const Info = ({ label, children, style }) => (
  <div style={style}>
    <div style={labelStyle}>{label}</div>
    <div style={{ fontSize: '.88rem', lineHeight: 1.5 }}>{children}</div>
  </div>
);

// ── Paper trail list ──────────────────────────────────────────────────────────
function Trail({ order, showHidden, notify, onChanged }) {
  const [open, setOpen] = useState('');
  const [editing, setEditing] = useState('');
  const list = [...(order.evidence || [])]
    .filter((e) => showHidden || !e.hidden)
    .sort((a, b) => String(a.sent_at || a.added_at).localeCompare(String(b.sent_at || b.added_at)));
  if (!list.length) return <div style={{ fontSize: '.85rem', color: '#d97706', marginBottom: 10 }}><AlertTriangle size={13} /> Nothing on file yet. Add the emails that agreed this below — that is what settles it when someone asks next year.</div>;

  const hide = async (e, hidden) => {
    const r = await api('updateEvidence', { id: order.id, evidenceId: e.id, hidden });
    if (!r.success) notify && notify(r.error || 'Could not update', 'error');
    onChanged();
  };

  return (
    <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
      {list.map((e) => {
        const K = KIND[e.kind] || KIND.note;
        const isOpen = open === e.id;
        if (editing === e.id) {
          return <EvidenceEditor key={e.id} orderId={order.id} e={e} notify={notify} onDone={() => { setEditing(''); onChanged(); }} onCancel={() => setEditing('')} />;
        }
        return (
          <div key={e.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: '10px 12px', opacity: e.hidden ? 0.5 : 1 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <K.icon size={15} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{e.subject || K.label}{e.hidden ? ' (hidden)' : ''}</div>
                <div style={{ fontSize: '.8rem', marginTop: 2 }}>
                  {e.from_name || e.from_email ? <><strong>{e.from_name || e.from_email}</strong>{e.from_name && e.from_email ? ` <${e.from_email}>` : ''}</> : <span style={{ color: 'var(--hp-dim)' }}>Sender not recorded</span>}
                  {e.organisation ? ` · ${e.organisation}` : ''}
                </div>
                <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                  {e.sent_at ? fmtWhen(e.sent_at) : 'Date/time not recorded'}{e.to ? ` · to ${e.to}` : ''}
                </div>
                {e.summary && <div style={{ fontSize: '.85rem', marginTop: 6, whiteSpace: 'pre-wrap' }}>{e.summary}</div>}
                {isOpen && e.body && <div style={{ fontSize: '.8rem', marginTop: 8, whiteSpace: 'pre-wrap', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: 10, maxHeight: 360, overflowY: 'auto' }}>{e.body}</div>}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: '.78rem' }}>
                  {e.file_url && <a href={e.file_url} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ExternalLink size={12} /> {e.file_name || 'Open the original'}</a>}
                  {e.body && <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => setOpen(isOpen ? '' : e.id)}>{isOpen ? 'Hide the email text' : 'Read the email text'}</button>}
                  <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => setEditing(e.id)}><Pencil size={11} /> Correct details</button>
                  {e.hidden
                    ? <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => hide(e, false)}><RotateCcw size={11} /> Restore</button>
                    : <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', color: 'var(--hp-dim)' }} onClick={() => hide(e, true)}><EyeOff size={11} /> Hide</button>}
                  <span style={{ color: 'var(--hp-dim)', marginLeft: 'auto' }}>filed by {e.added_by} {fmtDay(e.added_at)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// The metadata fields of one record, used for adding and for correcting.
function EvidenceFields({ ev, set }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 140px' }}>
          <label style={labelStyle}>Kind</label>
          <select className="input" style={inputStyle} value={ev.kind} onChange={(e) => set({ kind: e.target.value })}>
            {Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Subject / heading</label>
          <input className="input" style={inputStyle} value={ev.subject} onChange={(e) => set({ subject: e.target.value })} placeholder="RE: CBS courier deliveries" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>From — person</label>
          <input className="input" style={inputStyle} value={ev.from_name} onChange={(e) => set({ from_name: e.target.value })} placeholder="Cara" />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Their email</label>
          <input className="input" style={inputStyle} value={ev.from_email} onChange={(e) => set({ from_email: e.target.value })} placeholder="name@auspost.com.au" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Organisation</label>
          <input className="input" style={inputStyle} value={ev.organisation} onChange={(e) => set({ organisation: e.target.value })} placeholder="Australia Post — Alexandria PDC" />
        </div>
        <div style={{ flex: '0 1 220px' }}>
          <label style={labelStyle}>Sent (date &amp; time)</label>
          <input type="datetime-local" className="input" style={inputStyle} value={toLocalInput(ev.sent_at)} onChange={(e) => set({ sent_at: fromLocalInput(e.target.value) })} />
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={labelStyle}>To / cc</label>
        <input className="input" style={inputStyle} value={ev.to} onChange={(e) => set({ to: e.target.value })} placeholder="Paul; cc Brad, Val, Jayda" />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={labelStyle}>What it says, in a line or two</label>
        <textarea className="input" rows={2} style={inputStyle} value={ev.summary} onChange={(e) => set({ summary: e.target.value })} placeholder="AP confirms: deliver CBS parcels to the CBP North courier desk; a third party moves them on." />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={labelStyle}>Email text (optional — paste it so it can be searched)</label>
        <textarea className="input" rows={4} style={inputStyle} value={ev.body} onChange={(e) => set({ body: e.target.value })} />
      </div>
    </>
  );
}

const blankEvidence = () => ({ kind: 'email', subject: '', from_name: '', from_email: '', organisation: '', sent_at: '', to: '', summary: '', body: '' });

function AddEvidence({ orderId, notify, onAdded }) {
  const [open, setOpen] = useState(false);
  const [ev, setEv] = useState(blankEvidence());
  const [file, setFile] = useState(null); // { base64, name, contentType }
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (p) => setEv((x) => ({ ...x, ...p }));

  const takeFile = async (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { notify && notify(`That file is over ${MAX_MB} MB — save the email as PDF or a screenshot instead`, 'error'); return; }
    const buf = await f.arrayBuffer();
    const contentType = f.type || (isEml(f) ? 'message/rfc822' : 'application/octet-stream');
    setFile({ base64: arrayBufferToBase64(buf), name: f.name, contentType });
    setOpen(true);
    if (isEml(f)) {
      // A saved email carries its own sender, date and subject — take them from it rather
      // than have someone retype them from a screenshot.
      const parsed = parseEml(new TextDecoder('utf-8').decode(buf));
      setEv((x) => ({ ...x, kind: 'email', ...Object.fromEntries(Object.entries(parsed).filter(([, v]) => v)) }));
    } else if (!ev.subject) {
      set({ kind: /pdf|image/.test(contentType) ? 'email' : 'file' });
    }
  };

  const save = async () => {
    if (!file && !ev.subject && !ev.summary && !ev.body) { notify && notify('Add a file, or at least a subject or summary', 'error'); return; }
    setBusy(true);
    try {
      const r = await api('addEvidence', { id: orderId, evidence: ev, file });
      if (r.success) { setEv(blankEvidence()); setFile(null); setOpen(false); onAdded(); notify && notify('Filed on the paper trail', 'success'); }
      else notify && notify(r.error || 'Could not file it', 'error');
    } catch (e) { notify && notify('Network error — nothing was filed', 'error'); }
    setBusy(false);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); takeFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}
      style={{ border: `2px dashed ${over ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10, padding: 14, marginTop: 6 }}
    >
      {!open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <UploadCloud size={22} style={{ color: 'var(--hp-gold)' }} />
          <div style={{ flex: 1, fontSize: '.84rem' }}>
            <strong>Add to the paper trail</strong> — drop an email here.<br />
            <span style={{ color: 'var(--hp-dim)' }}>Best: in Gmail open the email → ⋮ → <em>Download message</em>, drop the <code>.eml</code> — sender, date/time and subject fill in exactly. A PDF or screenshot works too; type the details.</span>
          </div>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            Choose file<input type="file" accept=".eml,message/rfc822,application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => takeFile(e.target.files && e.target.files[0])} />
          </label>
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>No file — just a note</button>
        </div>
      ) : (
        <>
          {file && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.84rem', marginBottom: 10 }}>
              <FileText size={14} style={{ color: 'var(--hp-gold)' }} /> {file.name}
              {/\.eml$/i.test(file.name) && <span style={{ color: '#16a34a', fontSize: '.76rem' }}>· read from the email itself — check and file</span>}
              <button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}><X size={12} /></button>
            </div>
          )}
          <EvidenceFields ev={ev} set={set} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? <Loader size={13} /> : <Save size={13} />} File it</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setFile(null); setEv(blankEvidence()); }}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

function EvidenceEditor({ orderId, e, notify, onDone, onCancel }) {
  const [ev, setEv] = useState({ ...blankEvidence(), ...e });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const { kind, subject, from_name, from_email, organisation, sent_at, to, summary, body } = ev;
    const r = await api('updateEvidence', { id: orderId, evidenceId: e.id, evidence: { kind, subject, from_name, from_email, organisation, sent_at, to, summary, body } });
    setBusy(false);
    if (r.success) onDone(); else notify && notify(r.error || 'Could not save', 'error');
  };
  return (
    <div style={{ border: '1px solid var(--hp-gold)', borderRadius: 8, padding: 12 }}>
      <EvidenceFields ev={ev} set={(p) => setEv((x) => ({ ...x, ...p }))} />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}><Save size={13} /> Save</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Add / edit form ───────────────────────────────────────────────────────────
function OrderForm({ initial, id, inboxId, drivers, notify, onCancel, onSaved }) {
  const [f, setF] = useState(initial);
  const [alertNow, setAlertNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState(null);
  const [addLogin, setAddLogin] = useState('');
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const setPlace = (k, p) => setF((x) => ({ ...x, [k]: { ...x[k], ...p } }));
  const setDriver = (login, p) => setF((x) => ({ ...x, drivers: x.drivers.map((d) => (d.login === login ? { ...d, ...p } : d)) }));

  const addDriver = (login, role = 'cover', via = '') => {
    const L = (login || '').toUpperCase().trim();
    if (!L) return;
    setF((x) => {
      const cur = x.drivers.find((d) => d.login === L);
      if (cur) {
        // On both lists → they deliver both ends.
        if (cur.role !== role && role !== 'cover' && cur.role !== 'cover') return { ...x, drivers: x.drivers.map((d) => (d.login === L ? { ...d, role: 'both' } : d)) };
        return x;
      }
      return { ...x, drivers: [...x.drivers, { login: L, role, note: '', via }] };
    });
  };

  const find = async () => {
    setFinding(true);
    const r = await api('suggest', { kind: f.kind, from: f.from, to: f.to });
    setFinding(false);
    if (!r.success) { notify && notify(r.error || 'Could not search the routes', 'error'); return; }
    setFound(r);
    r.from.forEach((d) => addDriver(d.login, 'from', d.via));
    r.to.forEach((d) => addDriver(d.login, 'to', d.via));
  };

  // From the inbox the addresses are already filled — look the drivers up straight away so the
  // office is checking a full draft, not assembling one.
  useEffect(() => {
    if (inboxId && !f.drivers.length && f.from.address && f.to.address) find();
  }, []); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const r = await api('save', { id, fields: f });
      if (!r.success) { notify && notify(r.error || 'Could not save', 'error'); setBusy(false); return; }
      if (inboxId) {
        // Made from a forwarded email: file every message in that chain onto the new order's
        // paper trail, then (if ticked) tell the drivers and team leaders straight away.
        const fr = await api('inboxFile', { id: inboxId, orderId: r.id, notify: alertNow && f.drivers.length > 0 });
        if (!fr.success) notify && notify(`Order saved, but the email was not filed: ${fr.error}`, 'error');
        else notify && notify(`Saved — ${fr.records} record${fr.records === 1 ? '' : 's'} filed${fr.sent && fr.sent.results ? `; alerted ${fr.sent.results.map((x) => `${x.login} (${x.how})`).join(', ')}` : ''}`, 'success');
      } else if (!id && alertNow && f.drivers.length) {
        const sr = await api('notify', { id: r.id });
        if (sr.success) notify && notify(`Saved — alerted ${sr.results.map((x) => `${x.login} (${x.how})`).join(', ')}`, 'success');
        else notify && notify(`Saved, but the drivers were not alerted: ${sr.error}`, 'error');
      }
      onSaved(r.id);
    } catch (e) { notify && notify('Network error — not saved', 'error'); }
    setBusy(false);
  };

  const placeEditor = (k, tone, title, hint) => (
    <div style={{ border: `2px solid ${tone}`, borderRadius: 10, padding: 12 }}>
      <div style={{ ...labelStyle, color: tone }}>{title}</div>
      <input className="input" style={{ ...inputStyle, marginBottom: 6 }} value={f[k].name} onChange={(e) => setPlace(k, { name: e.target.value })} placeholder={hint.name} />
      <input className="input" style={inputStyle} value={f[k].address} onChange={(e) => setPlace(k, { address: e.target.value })} placeholder={hint.address} />
    </div>
  );

  const care = f.kind === 'care';
  const atl = oneAddress(f.kind);   // one address, drivers who deliver it — ATL and take-special-care
  return (
    <div>
      {care ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {placeEditor('from', '#dc2626', 'Take special care at (name, then address)', { name: 'House / building name (optional)', address: '33 Mitchell Street, Glebe NSW 2037' })}
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
            Where the parcels go does not change. The driver on this round sees it red on the shift screen every working day, gets the 6am push, and taps Got it. Whoever takes the round over gets it automatically.
          </div>
          <div style={{ flex: '0 1 200px' }}>
            <label style={labelStyle}>AP case number (optional)</label>
            <input className="input" style={{ ...inputStyle, maxWidth: 200 }} value={f.case_number} onChange={(e) => set({ case_number: e.target.value })} placeholder="74452939" />
          </div>
        </div>
      ) : atl ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {placeEditor('from', '#16a34a', 'Authority to Leave — every parcel for (name, then address)', { name: 'House / building name (optional)', address: '104 Mitchell Street, Glebe NSW 2037' })}
          <div>
            <label style={labelStyle}>Leave them in (drivers read this)</label>
            <input className="input" style={inputStyle} value={f.receptacle} onChange={(e) => set({ receptacle: e.target.value })} placeholder="the parcel receptacle at the front door" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.86rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.include_signature !== false} onChange={(e) => set({ include_signature: e.target.checked })} />
            Includes signature items — leave them too, never card them
          </label>
          <div style={{ border: `1px solid ${f.disability ? '#2563eb' : 'var(--hp-border)'}`, borderRadius: 10, padding: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem', fontWeight: 700, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.disability} onChange={(e) => set({ disability: e.target.checked })} />
              <Accessibility size={15} style={{ color: '#2563eb' }} /> Customer with a disability (AP Accessibility &amp; Disability accommodation)
            </label>
            {f.disability && (
              <div style={{ marginTop: 8 }}>
                <label style={labelStyle}>What AP approved, and why (office only)</label>
                <textarea className="input" rows={3} style={inputStyle} value={f.ad_details} onChange={(e) => set({ ad_details: e.target.value })}
                  placeholder="Customer has mobility limitations and cannot attend the Post Office. Delivery point assessed; ATL approved by the Facility Manager." />
              </div>
            )}
          </div>
          <div style={{ flex: '0 1 200px' }}>
            <label style={labelStyle}>AP case number (optional)</label>
            <input className="input" style={{ ...inputStyle, maxWidth: 200 }} value={f.case_number} onChange={(e) => set({ case_number: e.target.value })} placeholder="74101158" />
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {placeEditor('from', '#dc2626', 'Parcels addressed to (building name, then address)', { name: 'CBS Building', address: '35 Tumbalong Boulevard, Haymarket NSW 2000' })}
          <div style={{ textAlign: 'center', color: 'var(--hp-gold)', fontWeight: 800 }}>↓ are delivered instead to</div>
          {placeEditor('to', '#16a34a', 'Deliver to (building / desk, then address)', { name: 'CBP North Building — courier desk', address: '1-11 Harbour Street, Sydney NSW 2000' })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Customer</label>
          <input className="input" style={inputStyle} value={f.customer} onChange={(e) => set({ customer: e.target.value })} placeholder="Commonwealth Bank (CBA)" />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Customer contact</label>
          <input className="input" style={inputStyle} value={f.contact} onChange={(e) => set({ contact: e.target.value })} placeholder="Courier & Mailroom Support team" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        <div style={{ flex: '0 1 180px' }}>
          <label style={labelStyle}>In force from</label>
          <input type="date" className="input" style={inputStyle} value={f.starts_on} onChange={(e) => set({ starts_on: e.target.value })} />
        </div>
        <div style={{ flex: '0 1 180px' }}>
          <label style={labelStyle}>Review on (optional)</label>
          <input type="date" className="input" style={inputStyle} value={f.review_on} onChange={(e) => set({ review_on: e.target.value })} />
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>What it covers (drivers see this, small)</label>
        <textarea className="input" rows={2} style={inputStyle} value={f.applies_to} onChange={(e) => set({ applies_to: e.target.value })} placeholder="Everything addressed to Commonwealth Bank / CBS at 35 Tumbalong Blvd — envelopes, satchels, small parcels and large boxes." />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={labelStyle}>{care ? 'What the driver must do — calm, plain words, no names or allegations (required). What happened goes in the office notes.' : 'Instruction for the drivers — short, plain words'}</label>
        <textarea className="input" rows={care ? 4 : 3} style={inputStyle} value={f.instruction} onChange={(e) => set({ instruction: e.target.value })}
          placeholder={care
            ? 'Please take extra care with every delivery to this address.\nHand the parcel to the customer and get their signature. If nobody is home, card it and take it to the Post Office.'
            : 'Hand everything to the courier desk. Do not take any parcel to 35 Tumbalong Blvd — the mailroom moves them on.'} />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={labelStyle}>Office notes (never shown to drivers)</label>
        <textarea className="input" rows={2} style={inputStyle} value={f.office_notes} onChange={(e) => set({ office_notes: e.target.value })} />
      </div>

      <div style={sectionTitle}><Users size={14} /> Drivers to tell</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <button className="btn btn-secondary btn-sm" disabled={finding || !f.from.address || (!atl && !f.to.address)} onClick={find}>
          {finding ? <Loader size={13} /> : <Wand2 size={13} />} Find drivers from the routes
        </button>
        <select className="input" style={{ ...inputStyle, width: 220 }} value={addLogin} onChange={(e) => setAddLogin(e.target.value)}>
          <option value="">Add a driver…</option>
          {drivers.map((d) => <option key={d.login} value={d.login}>{d.login}{d.name ? ` — ${d.name}` : ''}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" disabled={!addLogin} onClick={() => { addDriver(addLogin, atl ? 'from' : 'cover'); setAddLogin(''); }}><Plus size={13} /> Add</button>
      </div>
      {found && atl && (
        <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
          On the route list of: {found.from.length ? found.from.map((d) => `${d.login} (${d.via})`).join(', ') : 'nobody — add the driver by hand'}
        </div>
      )}
      {found && !atl && (
        <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
          Addressed-to: {found.from.length ? found.from.map((d) => `${d.login} (${d.via})`).join(', ') : 'no route lists this street'} ·
          Deliver-to: {found.to.length ? found.to.map((d) => `${d.login} (${d.via})`).join(', ') : 'no route lists this street'}
        </div>
      )}
      <div style={{ display: 'grid', gap: 6 }}>
        {f.drivers.map((d) => (
          <div key={d.login} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, minWidth: 110 }}>{d.login}</span>
              <select className="input" style={{ ...inputStyle, width: 260 }} value={d.role} onChange={(e) => setDriver(d.login, { role: e.target.value, route: '' })}>
                {Object.entries(atl ? ATL_ROLE_LABEL : ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setF((x) => ({ ...x, drivers: x.drivers.filter((y) => y.login !== d.login) }))}><X size={13} /></button>
            </div>
            <input className="input" style={{ ...inputStyle, marginTop: 6 }} value={d.note} onChange={(e) => setDriver(d.login, { note: e.target.value })} placeholder={`Extra line just for ${d.login} (optional)`} />
          </div>
        ))}
      </div>
      {id && <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 8 }}>Changing the addresses, instruction or drivers puts it back in front of every driver as unread.</div>}

      {(inboxId || !id) && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: '.86rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={alertNow} onChange={(e) => setAlertNow(e.target.checked)} />
          Alert the drivers and their team leaders as soon as it&rsquo;s saved (push, or a text if they have no alerts on)
        </label>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? <Loader size={14} /> : <Save size={14} />} {id ? 'Save changes' : care ? 'Save take-special-care address' : atl ? 'Save Authority to Leave' : 'Save standing order'}</button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Inbox ─────────────────────────────────────────────────────────────────────
// Emails forwarded to enquiriesphb+standing@gmail.com (picked up within five minutes) or
// dropped here, already read into a draft. Triage is the office's: check the draft, make it
// an order (or add it to one), and the drivers are told. Nothing reaches a driver from here.
const INBOX_ADDRESS = 'enquiriesphb+standing@gmail.com';

function draftFrom(it) {
  const p = (it.extracted && it.extracted.proposal) || {};
  const base = emptyFields();
  return {
    ...base,
    customer: p.customer || '', contact: p.contact || '', applies_to: p.applies_to || '',
    instruction: p.instruction || '', office_notes: p.office_notes || '',
    from: { name: (p.from && p.from.name) || '', address: (p.from && p.from.address) || '' },
    to: { name: (p.to && p.to.name) || '', address: (p.to && p.to.address) || '' },
    starts_on: p.starts_on || base.starts_on,
  };
}

function InboxPanel({ items, orders, notify, onChanged, onTriage }) {
  const [showDone, setShowDone] = useState(false);
  const [over, setOver] = useState(false);
  const [reading, setReading] = useState(false);
  const open = items.filter((i) => i.status === 'new');
  const done = items.filter((i) => i.status !== 'new');

  const take = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { notify && notify(`Over ${MAX_MB} MB — forward the email instead`, 'error'); return; }
    const buf = await file.arrayBuffer();
    const ct = file.type || (isEml(file) ? 'message/rfc822' : 'application/octet-stream');
    const att = { filename: file.name, contentType: ct, dataBase64: arrayBufferToBase64(buf) };
    let payload;
    if (isEml(file)) {
      const m = parseEml(new TextDecoder('utf-8').decode(buf));
      payload = { from: m.from_name ? `${m.from_name} <${m.from_email}>` : m.from_email, to: m.to, subject: m.subject, date: m.sent_at, text: m.body, attachments: [att] };
    } else {
      payload = { subject: file.name, text: '', attachments: [att] };
    }
    setReading(true);
    try {
      const r = await api('inboxUpload', { payload });
      if (!r.success) notify && notify(r.error || 'Could not read it', 'error');
      else if (r.error) notify && notify(r.error, 'error');
      onChanged();
    } catch (e) { notify && notify('Network error — nothing was stored', 'error'); }
    setReading(false);
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <InboxIcon size={17} style={{ color: 'var(--hp-gold)' }} />
        <strong>Emails to triage</strong>
        {open.length > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '0 8px', fontSize: '.75rem', fontWeight: 800 }}>{open.length}</span>}
        {done.length > 0 && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowDone((v) => !v)}>{showDone ? 'Hide handled' : `Show ${done.length} handled`}</button>}
      </div>
      <div style={{ border: `2px dashed ${over ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10, padding: 12, marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: '.84rem' }}>
        <Forward size={20} style={{ color: 'var(--hp-gold)' }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong>Forward the email to <code style={{ userSelect: 'all' }}>{INBOX_ADDRESS}</code></strong> — it appears here within 5 minutes, already read.
          <div style={{ color: 'var(--hp-dim)' }}>Type anything the drivers need above the forward (e.g. “Andres hands these to Renzo”). Or drop an .eml, PDF or screenshot on this box.</div>
        </div>
        {reading
          ? <span style={{ color: 'var(--hp-dim)' }}><Loader size={13} /> Reading it — about 15 seconds…</span>
          : <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>Choose file<input type="file" accept=".eml,message/rfc822,application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => take(e.target.files && e.target.files[0])} /></label>}
      </div>
      {open.length === 0 && !showDone && <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)', marginTop: 10 }}>Nothing waiting.</div>}
      <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
        {[...open, ...(showDone ? done : [])].map((it) => <InboxItem key={it.id} it={it} orders={orders} notify={notify} onChanged={onChanged} onTriage={onTriage} />)}
      </div>
    </div>
  );
}

function InboxItem({ it, orders, notify, onChanged, onTriage }) {
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState('');
  const [showText, setShowText] = useState(false);
  const x = it.extracted;
  const p = x && x.proposal;
  const act = async (action, payload, ok) => {
    setBusy(true);
    try {
      const r = await api(action, { id: it.id, ...payload });
      if (!r.success) notify && notify(r.error || 'Could not do that', 'error');
      else { if (r.error) notify && notify(r.error, 'error'); else if (ok) notify && notify(ok, 'success'); }
    } catch (e) { notify && notify('Network error', 'error'); }
    setBusy(false);
    onChanged();
  };
  const handled = it.status !== 'new';
  return (
    <div style={{ border: `1px solid ${handled ? 'var(--hp-border)' : 'var(--hp-gold)'}`, borderRadius: 10, padding: 12, opacity: handled ? 0.65 : 1 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '.92rem' }}>{it.subject || '(no subject)'}</strong>
        {it.status === 'filed' && <span style={{ fontSize: '.72rem', color: '#16a34a', fontWeight: 700 }}>✓ filed {fmtDay(it.handled_at)} by {it.handled_by}</span>}
        {it.status === 'dismissed' && <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontWeight: 700 }}>dismissed {fmtDay(it.handled_at)} by {it.handled_by}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '.75rem', color: 'var(--hp-dim)' }}>{it.source === 'upload' ? 'dropped on the page' : 'forwarded'} · {fmtWhen(it.received_at)}</span>
      </div>
      <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>{it.forwarded_by ? `From ${it.forwarded_by}` : ''}{it.sent_at ? ` · sent ${fmtWhen(it.sent_at)}` : ''}</div>

      {it.reading && <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)', marginTop: 8 }}><Loader size={13} /> Reading it — refresh in a minute.</div>}
      {!it.reading && it.error && <div style={{ fontSize: '.82rem', color: '#d97706', marginTop: 8 }}><AlertTriangle size={13} /> {it.error}</div>}

      {p && (p.from.address || p.to.address) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, fontSize: '.88rem' }}>
          {p.customer && <span style={{ fontWeight: 800 }}>{p.customer}:</span>}
          <span style={{ color: '#dc2626', fontWeight: 700 }}>{p.from.name ? `${p.from.name}, ` : ''}{p.from.address || '?'}</span>
          <span style={{ color: 'var(--hp-gold)', fontWeight: 800 }}>→</span>
          <span style={{ color: '#16a34a', fontWeight: 700 }}>{p.to.name ? `${p.to.name}, ` : ''}{p.to.address || '?'}</span>
        </div>
      )}
      {x && x.forward_note && <div style={{ fontSize: '.84rem', marginTop: 6 }}><strong>Note with it:</strong> {x.forward_note}</div>}
      {x && (x.messages || []).length > 0 && (
        <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
          {x.messages.map((m, i) => (
            <div key={i} style={{ fontSize: '.8rem', display: 'flex', gap: 6 }}>
              <Mail size={12} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 3 }} />
              <span><strong>{m.from_name || m.organisation || 'Unknown sender'}</strong>{m.organisation && m.from_name ? ` (${m.organisation})` : ''}
                <span style={{ color: 'var(--hp-dim)' }}> · {m.sent_at ? fmtWhen(m.sent_at) : (m.sent_text || 'date not shown')}</span> — {m.summary}</span>
            </div>
          ))}
        </div>
      )}
      {x && (x.questions || []).length > 0 && (
        <div style={{ marginTop: 8, fontSize: '.8rem', color: '#d97706' }}>
          <strong>Check:</strong> {x.questions.join(' · ')}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: '.78rem' }}>
        {(it.files || []).map((f) => <a key={f.file_path} href={f.file_url} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ExternalLink size={12} /> {f.file_name}</a>)}
        {it.text && <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => setShowText((v) => !v)}>{showText ? 'Hide the email' : 'Read the email'}</button>}
        {(it.omitted || []).length > 0 && <span style={{ color: '#d97706' }}>{it.omitted.length} attachment(s) too big to bring in — open the email in Gmail</span>}
      </div>
      {showText && <div style={{ fontSize: '.8rem', marginTop: 8, whiteSpace: 'pre-wrap', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: 10, maxHeight: 320, overflowY: 'auto' }}>{it.text}</div>}

      {!handled && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => onTriage(it)}><Plus size={13} /> Make it a standing order</button>
          <select className="input" style={{ ...inputStyle, width: 230 }} value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">…or add it to an existing order</option>
            {orders.map((o) => <option key={o.id} value={o.id}>{o.customer || short(o.from.address)}{o.status !== 'active' ? ' (ended)' : ''}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" disabled={busy || !target} onClick={() => act('inboxFile', { orderId: target }, 'Filed on that order’s paper trail')}>Add</button>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act('inboxReread', {}, 'Reading it again — refresh in a minute')}><RefreshCw size={12} /> Read again</button>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act('inboxDismiss', {}, 'Dismissed — Show handled to restore it')} style={{ marginLeft: 'auto', color: 'var(--hp-dim)' }}><EyeOff size={12} /> Dismiss</button>
        </div>
      )}
      {it.status === 'dismissed' && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={() => act('inboxDismiss', { restore: true })}><RotateCcw size={12} /> Restore</button>
      )}
    </div>
  );
}
